// Operator-facing copy for SEO research ideas. market_research findings carry a machine
// note (legacy "ci=…; signals=…" or explicit advertising/exploratory format) that was shown
// verbatim in Opportunities and copied into generated briefs. Metrics are already shown
// from evidence_summary, so only the qualifying signals are rewritten into plain words.

const SIGNAL_LABELS: Record<string, string> = {
  "volume > 100": "100+ monthly searches",
  "volume > 0": "some search demand",
  "competition_index < 20": "low advertising competition",
  "competition_index 20-40": "moderate advertising competition",
  "cpc > 5": "advertisers pay over $5 per click",
  "paa_questions non-empty": "searchers ask related questions",
  "community pain confirmed": "people discuss this problem in online communities",
};

const RAW_NOTE = /^.*?:\s*vol=[^,;]*,\s*ci=[^,;]*,\s*cpc=[^;]*;\s*signals=(.*?)\.?\s*$/s;
const EXPLORATORY_NOTE = /^.*?:\s*vol=[^,;]*,\s*ad_competition_index=[^,;]*,\s*cpc=[^;]*;\s*exploratory heuristic priority signals=(.*?)\.?\s*$/s;

function joinList(items: string[]): string {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

/**
 * Rewrite a raw market-research note. Returns "" when it lists no signals and null when
 * the text is not a raw research note (hand-written text is never rewritten).
 */
export function humanizeResearchNote(note: string): string | null {
  const trimmed = note.trim();
  const exploratory = EXPLORATORY_NOTE.exec(trimmed);
  const match = exploratory ?? RAW_NOTE.exec(trimmed);
  if (!match) return null;
  const signals = match[1]!.split(",").map(s => s.trim()).filter(s => s && s !== "none");
  if (!signals.length) return "";
  const label = exploratory ? "Exploratory signals" : "Why it qualified";
  return `${label}: ${joinList(signals.map(s => SIGNAL_LABELS[s] ?? s.replaceAll("_", " ")))}.`;
}

/** Replace a trailing raw "Research note: …" in an idea's proposed outcome with plain words. */
export function humanizeProposedOutcome(text: string): string {
  const at = text.lastIndexOf("Research note:");
  if (at < 0) return text;
  const humanized = humanizeResearchNote(text.slice(at + "Research note:".length));
  if (humanized === null) return text;
  return [text.slice(0, at).trim(), humanized].filter(Boolean).join(" ");
}

const TYPE_LABELS: Record<string, string> = {
  seo_article: "SEO article",
  bluesky_post: "Bluesky post",
  customer_skill: "Customer skill",
  directory_submission: "Directory submission",
  other: "Other",
};

export function opportunityTypeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type.replaceAll("_", " ").replace(/^./, c => c.toUpperCase());
}
